// client/src/components/SchoolsTable.jsx
import React, { useState } from 'react';
import { Table, Badge, Modal, Tab, Tabs, Button, Spinner } from 'react-bootstrap';
import * as XLSX from 'xlsx';

const SchoolsTable = ({ schools, allData }) => {
    const [selectedSchool, setSelectedSchool] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [activeTab, setActiveTab] = useState('subjects');
    const [sortConfig, setSortConfig] = useState({ key: 'total_views', direction: 'desc' });
    const [detailedRecords, setDetailedRecords] = useState([]);
    const [loadingDetails, setLoadingDetails] = useState(false);

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
        }));
    };

    const sortedSchools = [...schools].sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        
        if (typeof aVal === 'string') {
            aVal = aVal.toLowerCase();
            bVal = bVal.toLowerCase();
        }
        
        if (sortConfig.direction === 'asc') {
            return aVal > bVal ? 1 : -1;
        } else {
            return aVal < bVal ? 1 : -1;
        }
    });

    const getSortClass = (key) => {
        if (sortConfig.key === key) {
            return sortConfig.direction === 'asc' ? 'asc' : 'desc';
        }
        return '';
    };

    const exportToExcel = () => {
        const exportData = schools.map(school => ({
            'Школа': school.school_name,
            'Район': school.district,
            'Просмотры': school.total_views,
            'Пользователи': school.unique_users,
            'Ученики': school.unique_students,
            'Учителя': school.unique_teachers,
            'Предметы': school.subjects_count,
            'Классы': school.classes_count,
            'Поставщики': school.providers_count,
            'Дней активности': school.active_days
        }));
        
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Образовательные учреждения');
        XLSX.writeFile(wb, `ub_cok_schools_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    const handleSchoolClick = async (school) => {
        setSelectedSchool(school);
        setDetailedRecords([]);
        setLoadingDetails(true);
        setShowModal(true);
        
        try {
            const response = await fetch(`https://apitestubcok.paragraf.spb.ru/api/school-monitoring?schoolId=${school.id}&limit=5000`);
            const data = await response.json();
            if (data.success) {
                setDetailedRecords(data.records || []);
            } else {
                console.error('Ошибка загрузки:', data.error);
                setDetailedRecords([]);
            }
        } catch (error) {
            console.error('Ошибка загрузки детальных записей:', error);
            setDetailedRecords([]);
        } finally {
            setLoadingDetails(false);
        }
    };

    const getSubjectsStats = () => {
        if (!selectedSchool || !allData?.monitoringData) return [];
        
        const schoolData = allData.monitoringData.filter(
            record => record.school_id === selectedSchool.id
        );
        
        const subjectsMap = new Map();
        
        schoolData.forEach(record => {
            if (!record.subject) return;
            if (!subjectsMap.has(record.subject)) {
                subjectsMap.set(record.subject, {
                    subject: record.subject,
                    total_views: 0,
                    total_records: 0,
                    unique_users: new Set(),
                    dates: new Set(),
                    providers: new Set(),
                    classes: new Set()
                });
            }
            const stat = subjectsMap.get(record.subject);
            stat.total_views += record.views_count || 0;
            stat.total_records++;
            if (record.user_alias) stat.unique_users.add(record.user_alias);
            if (record.date) stat.dates.add(record.date);
            if (record.provider) stat.providers.add(record.provider);
            if (record.class_level) stat.classes.add(record.class_level);
        });
        
        return Array.from(subjectsMap.values())
            .map(s => ({
                ...s,
                unique_users: s.unique_users.size,
                dates_count: s.dates.size,
                providers_count: s.providers.size,
                providers_list: Array.from(s.providers).slice(0, 5).join(', '),
                classes_list: Array.from(s.classes).sort((a,b) => a-b).join(', ')
            }))
            .sort((a, b) => b.total_views - a.total_views);
    };

    const getProvidersStats = () => {
        if (!selectedSchool || !allData?.monitoringData) return [];
        
        const schoolData = allData.monitoringData.filter(
            record => record.school_id === selectedSchool.id
        );
        
        const providersMap = new Map();
        
        schoolData.forEach(record => {
            const provider = record.provider || 'Не указан';
            if (!providersMap.has(provider)) {
                providersMap.set(provider, {
                    provider: provider,
                    total_views: 0,
                    total_records: 0,
                    unique_users: new Set(),
                    subjects: new Set(),
                    dates: new Set(),
                    classes: new Set()
                });
            }
            const stat = providersMap.get(provider);
            stat.total_views += record.views_count || 0;
            stat.total_records++;
            if (record.user_alias) stat.unique_users.add(record.user_alias);
            if (record.subject) stat.subjects.add(record.subject);
            if (record.date) stat.dates.add(record.date);
            if (record.class_level) stat.classes.add(record.class_level);
        });
        
        return Array.from(providersMap.values())
            .map(p => ({
                ...p,
                unique_users: p.unique_users.size,
                subjects_count: p.subjects.size,
                subjects_list: Array.from(p.subjects).slice(0, 5).join(', '),
                classes_list: Array.from(p.classes).sort((a,b) => a-b).join(', '),
                dates_count: p.dates.size
            }))
            .sort((a, b) => b.total_views - a.total_views);
    };

    const getDatesStats = () => {
        if (!selectedSchool || !allData?.monitoringData) return [];
        
        const schoolData = allData.monitoringData.filter(
            record => record.school_id === selectedSchool.id
        );
        
        const datesMap = new Map();
        
        schoolData.forEach(record => {
            const date = record.date;
            if (!date) return;
            if (!datesMap.has(date)) {
                datesMap.set(date, {
                    date: date,
                    total_views: 0,
                    total_records: 0,
                    unique_users: new Set(),
                    subjects: new Set(),
                    providers: new Set(),
                    classes: new Set()
                });
            }
            const stat = datesMap.get(date);
            stat.total_views += record.views_count || 0;
            stat.total_records++;
            if (record.user_alias) stat.unique_users.add(record.user_alias);
            if (record.subject) stat.subjects.add(record.subject);
            if (record.provider) stat.providers.add(record.provider);
            if (record.class_level) stat.classes.add(record.class_level);
        });
        
        return Array.from(datesMap.values())
            .map(d => ({
                ...d,
                unique_users: d.unique_users.size,
                subjects_count: d.subjects.size,
                subjects_list: Array.from(d.subjects).slice(0, 5).join(', '),
                providers_count: d.providers.size,
                classes_list: Array.from(d.classes).sort((a,b) => a-b).join(', ')
            }))
            .sort((a, b) => b.date.localeCompare(a.date));
    };

    if (schools.length === 0) {
        return (
            <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <span><i className="bi bi-building me-2"></i>Образовательные учреждения</span>
                    <Badge bg="secondary">0</Badge>
                </div>
                <div className="text-center p-5 text-muted">
                    <i className="bi bi-inbox fs-1"></i>
                    <p className="mt-2">Нет данных</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <div>
                        <i className="bi bi-building me-2"></i>
                        Образовательные учреждения
                        <Badge bg="secondary" className="ms-2">{schools.length}</Badge>
                    </div>
                    <button className="export-btn" onClick={exportToExcel}>
                        <i className="bi bi-file-excel me-1"></i> Экспорт XLSX
                    </button>
                </div>
                <div className="table-responsive" style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
                    <Table hover className="mb-0" style={{ position: 'relative' }}>
                        <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 10 }}>
                            <tr>
                                <th className={`sortable ${getSortClass('school_name')}`} onClick={() => handleSort('school_name')}>Школа</th>
                                <th className={`sortable ${getSortClass('district')}`} onClick={() => handleSort('district')}>Район</th>
                                <th className={`sortable text-end ${getSortClass('total_views')}`} onClick={() => handleSort('total_views')}>Просмотры</th>
                                <th className={`sortable text-end ${getSortClass('unique_users')}`} onClick={() => handleSort('unique_users')}>Пользователи</th>
                                <th className={`sortable text-end ${getSortClass('unique_students')}`} onClick={() => handleSort('unique_students')}>Ученики</th>
                                <th className={`sortable text-end ${getSortClass('unique_teachers')}`} onClick={() => handleSort('unique_teachers')}>Учителя</th>
                                <th className={`sortable text-end ${getSortClass('subjects_count')}`} onClick={() => handleSort('subjects_count')}>Предметы</th>
                                <th className={`sortable text-end ${getSortClass('classes_count')}`} onClick={() => handleSort('classes_count')}>Классы</th>
                                <th className={`sortable text-end ${getSortClass('providers_count')}`} onClick={() => handleSort('providers_count')}>Поставщики</th>
                                <th className={`sortable text-end ${getSortClass('active_days')}`} onClick={() => handleSort('active_days')}>Дней</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedSchools.map((school) => (
                                <tr key={school.id} className="school-row" onClick={() => handleSchoolClick(school)}>
                                    <td><strong>{school.school_name}</strong></td>
                                    <td>{school.district || '—'}</td>
                                    <td className="text-end">{(school.total_views || 0).toLocaleString()}</td>
                                    <td className="text-end">{(school.unique_users || 0).toLocaleString()}</td>
                                    <td className="text-end">{(school.unique_students || 0).toLocaleString()}</td>
                                    <td className="text-end">{(school.unique_teachers || 0).toLocaleString()}</td>
                                    <td className="text-end">{school.subjects_count || 0}</td>
                                    <td className="text-end">{school.classes_count || 0}</td>
                                    <td className="text-end">{school.providers_count || 0}</td>
                                    <td className="text-end">{school.active_days || 0}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            </div>

            <Modal show={showModal} onHide={() => setShowModal(false)} size="xl" centered>
                {selectedSchool && (
                    <>
                        <Modal.Header closeButton>
                            <Modal.Title>
                                <div>
                                    <i className="bi bi-building me-2"></i>
                                    {selectedSchool.school_name}
                                    <Badge bg="secondary" className="ms-2">{selectedSchool.district}</Badge>
                                </div>
                                <div className="small text-muted mt-1">
                                    <i className="bi bi-eye me-1"></i>{(selectedSchool.total_views || 0).toLocaleString()} просмотров
                                    <span className="mx-2">•</span>
                                    <i className="bi bi-people me-1"></i>{(selectedSchool.unique_users || 0).toLocaleString()} пользователей
                                    <span className="mx-2">•</span>
                                    <i className="bi bi-truck me-1"></i>{selectedSchool.providers_count || 0} поставщиков
                                </div>
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
                                <Tab eventKey="subjects" title="По предметам">
                                    <div className="table-responsive">
                                        <Table striped hover size="sm">
                                            <thead>
                                                <tr>
                                                    <th>Предмет</th>
                                                    <th className="text-end">Просмотры</th>
                                                    <th className="text-end">Записей</th>
                                                    <th className="text-end">Пользователей</th>
                                                    <th className="text-end">Дней</th>
                                                    <th>Классы</th>
                                                    <th>Поставщики</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {getSubjectsStats().map((stat, idx) => (
                                                    <tr key={idx}>
                                                        <td><strong>{stat.subject}</strong></td>
                                                        <td className="text-end">{stat.total_views.toLocaleString()}</td>
                                                        <td className="text-end">{stat.total_records.toLocaleString()}</td>
                                                        <td className="text-end">{stat.unique_users.toLocaleString()}</td>
                                                        <td className="text-end">{stat.dates_count}</td>
                                                        <td className="small">{stat.classes_list || '—'}</td>
                                                        <td className="small">{stat.providers_list || '—'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Tab>
                                
                                <Tab eventKey="providers" title="По поставщикам">
                                    <div className="table-responsive">
                                        <Table striped hover size="sm">
                                            <thead>
                                                <tr>
                                                    <th>Поставщик</th>
                                                    <th className="text-end">Просмотры</th>
                                                    <th className="text-end">Записей</th>
                                                    <th className="text-end">Пользователей</th>
                                                    <th className="text-end">Предметов</th>
                                                    <th>Предметы</th>
                                                    <th className="text-end">Дней</th>
                                                    <th>Классы</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {getProvidersStats().map((stat, idx) => (
                                                    <tr key={idx}>
                                                        <td><strong>{stat.provider}</strong></td>
                                                        <td className="text-end">{stat.total_views.toLocaleString()}</td>
                                                        <td className="text-end">{stat.total_records.toLocaleString()}</td>
                                                        <td className="text-end">{stat.unique_users.toLocaleString()}</td>
                                                        <td className="text-end">{stat.subjects_count}</td>
                                                        <td className="small">{stat.subjects_list || '—'}</td>
                                                        <td className="text-end">{stat.dates_count}</td>
                                                        <td className="small">{stat.classes_list || '—'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Tab>
                                
                                <Tab eventKey="dates" title="По датам">
                                    <div className="table-responsive">
                                        <Table striped hover size="sm">
                                            <thead>
                                                <tr>
                                                    <th>Дата</th>
                                                    <th className="text-end">Просмотры</th>
                                                    <th className="text-end">Записей</th>
                                                    <th className="text-end">Пользователей</th>
                                                    <th className="text-end">Предметов</th>
                                                    <th>Предметы</th>
                                                    <th className="text-end">Поставщиков</th>
                                                    <th>Классы</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {getDatesStats().map((stat, idx) => (
                                                    <tr key={idx}>
                                                        <td>{new Date(stat.date).toLocaleDateString('ru-RU')}</td>
                                                        <td className="text-end">{stat.total_views.toLocaleString()}</td>
                                                        <td className="text-end">{stat.total_records.toLocaleString()}</td>
                                                        <td className="text-end">{stat.unique_users.toLocaleString()}</td>
                                                        <td className="text-end">{stat.subjects_count}</td>
                                                        <td className="small">{stat.subjects_list || '—'}</td>
                                                        <td className="text-end">{stat.providers_count}</td>
                                                        <td className="small">{stat.classes_list || '—'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Tab>
                                
                                <Tab eventKey="records" title="Детальные записи">
                                    <div>
                                        <div className="mb-2 text-muted small">
                                            <i className="bi bi-info-circle me-1"></i>
                                            Всего записей: {detailedRecords.length}
                                        </div>
                                        {loadingDetails ? (
                                            <div className="text-center p-5">
                                                <Spinner animation="border" variant="primary" />
                                                <p className="mt-2">Загрузка записей...</p>
                                            </div>
                                        ) : (
                                            <div className="table-responsive" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                                                <Table striped hover size="sm">
                                                    <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
                                                        <tr>
                                                            <th>Дата</th>
                                                            <th>Предмет</th>
                                                            <th>Поставщик</th>
                                                            <th>Класс</th>
                                                            <th>Роль</th>
                                                            <th className="text-end">Просмотры</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {detailedRecords.length > 0 ? (
                                                            detailedRecords.map((record, idx) => (
                                                                <tr key={idx}>
                                                                    <td>{new Date(record.date).toLocaleDateString('ru-RU')}</td>
                                                                    <td>{record.subject || '—'}</td>
                                                                    <td>{record.provider || '—'}</td>
                                                                    <td>{record.class_level || '—'}</td>
                                                                    <td>
                                                                        <Badge bg={record.role === 'student' ? 'success' : 'primary'}>
                                                                            {record.role === 'student' ? 'Ученик' : 'Учитель'}
                                                                        </Badge>
                                                                    </td>
                                                                    <td className="text-end">{record.views_count || 0}</td>
                                                                </tr>
                                                            ))
                                                        ) : (
                                                            <tr>
                                                                <td colSpan="6" className="text-center text-muted py-4">
                                                                    Нет детальных записей
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        )}
                                    </div>
                                </Tab>
                            </Tabs>
                        </Modal.Body>
                        <Modal.Footer>
                            <div className="text-muted small">
                                <i className="bi bi-info-circle me-1"></i>
                                Всего записей: {detailedRecords.length}
                            </div>
                            <Button variant="secondary" onClick={() => setShowModal(false)}>
                                Закрыть
                            </Button>
                        </Modal.Footer>
                    </>
                )}
            </Modal>
        </>
    );
};

export default SchoolsTable;